import { useEffect, useMemo, useState } from "react";
import {
  getAllFilterCompanyService,
  getSellersByCompanyIdsService,
  getSellerWarehouseService,
} from "../../../services/company.service";

// Same requests the Edit Service Rate Card screen uses to fill its Company / Seller / Warehouse lists.
const COMPANY_REQUEST = { pageIndex: 1, pageSize: 10, sortColumn: "name", sortOrder: "ascending" };

const byLabel = (a, b) => String(a.label).localeCompare(String(b.label));

const parseIds = (csv) =>
  String(csv ?? "")
    .split(",")
    .map(Number)
    .filter((id) => id > 0);

const toCompanyOption = (company) => ({ value: company.Id, label: company.name });

const toSellerOption = (seller) => ({
  value: seller.id,
  label: seller.sellerAndCompany || seller.newSellerName || String(seller.id),
  companyIds: parseIds(seller.companyIds),
});

// Dropdown loads never reject: a failed list just stays empty and the report still works.
const loadCompanies = async () => {
  try {
    const res = await getAllFilterCompanyService(COMPANY_REQUEST);
    return (res?.data?.Entity?.listCompanyMasterOrdersEntity || []).map(toCompanyOption).sort(byLabel);
  } catch {
    return [];
  }
};

const loadSellers = async (companyIds) => {
  try {
    const res = await getSellersByCompanyIdsService({ companyIds });
    if (res?.data?.Status !== 200) return [];
    return (res.data.Entity?.listSellerMasterOrdersEntity || []).map(toSellerOption).sort(byLabel);
  } catch {
    return [];
  }
};

const loadSellerWarehouses = async (companyId, sellerId) => {
  try {
    const res = await getSellerWarehouseService({ companyId, sellerId, tenantId: 0 });
    return res?.data?.Status === 200 ? res.data.EntityList || [] : [];
  } catch {
    return [];
  }
};

// A seller can belong to several companies; only ask for the companies that are in scope.
const getCompanyScope = (seller, companyIds) => {
  const shared = seller.companyIds.filter((id) => companyIds.includes(id));
  return shared.length ? shared : companyIds;
};

const loadWarehouses = async (sellers, companyIds) => {
  const pairs = sellers.flatMap((seller) =>
    getCompanyScope(seller, companyIds).map((companyId) => ({ companyId, sellerId: seller.value }))
  );
  const lists = await Promise.all(pairs.map(({ companyId, sellerId }) => loadSellerWarehouses(companyId, sellerId)));
  const byId = new Map();
  lists.flat().forEach((item) => {
    if (item.Warehouse_Id && !byId.has(item.Warehouse_Id)) {
      byId.set(item.Warehouse_Id, { value: item.Warehouse_Id, label: item.WarehouseName });
    }
  });
  return [...byId.values()].sort(byLabel);
};

const useIdList = (ids) => {
  const key = ids.join(",");
  return useMemo(() => (key ? key.split(",").map(Number) : []), [key]);
};

// Company -> Seller -> Warehouse: each list is narrowed by the selection above it.
// Warehouses are only listed for the selected sellers, one request per seller and company.
export const usePnlEntityOptions = (enabled, selectedCompanyIds, selectedSellerIds) => {
  const [companyOptions, setCompanyOptions] = useState([]);
  const [sellerOptions, setSellerOptions] = useState([]);
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const companyIds = useIdList(selectedCompanyIds);
  const sellerIds = useIdList(selectedSellerIds);

  useEffect(() => {
    if (!enabled) return undefined;
    let ignore = false;
    void loadCompanies().then((options) => {
      if (!ignore) setCompanyOptions(options);
    });
    return () => {
      ignore = true;
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return undefined;
    if (companyIds.length === 0) {
      setSellerOptions([]);
      return undefined;
    }
    let ignore = false;
    void loadSellers(companyIds).then((options) => {
      if (!ignore) setSellerOptions(options);
    });
    return () => {
      ignore = true;
    };
  }, [enabled, companyIds]);

  useEffect(() => {
    if (!enabled) return undefined;
    const scope = sellerOptions.filter((seller) => sellerIds.includes(seller.value));
    if (companyIds.length === 0 || scope.length === 0) {
      setWarehouseOptions([]);
      return undefined;
    }
    let ignore = false;
    void loadWarehouses(scope, companyIds).then((options) => {
      if (!ignore) setWarehouseOptions(options);
    });
    return () => {
      ignore = true;
    };
  }, [enabled, companyIds, sellerIds, sellerOptions]);

  return { companyOptions, sellerOptions, warehouseOptions };
};
